import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Users, Play, Bell, Star, MessageCircle } from 'lucide-react';
import { api } from '../../api';

interface Conference {
  id: string;
  title: string;
  speaker: string;
  speakerTitle: string;
  date: string;
  time: string;
  duration: string;
  category: string;
  attendees?: number;
  views?: number;
  rating?: number;
  description: string;
  image: string;
  isLive?: boolean;
  price?: string;
}

interface Expert {
  id: string;
  name: string;
  title: string;
  speciality: string;
  experience: string;
  image: string;
  bio: string;
}

const ShowcaseConferences: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [upcomingConferences, setUpcomingConferences] = useState<Conference[]>([]);
  const [pastConferences, setPastConferences] = useState<Conference[]>([]);
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [upcomingResponse, pastResponse, expertsResponse] = await Promise.all([
          api.get('/siteContent/conferences/upcoming-conferences'),
          api.get('/siteContent/conferences/past-conferences'),
          api.get('/siteContent/experts'),
        ]);
        setUpcomingConferences(upcomingResponse.data || []);
        setPastConferences(pastResponse.data || []);
        setExperts(expertsResponse.data || []);
      } catch (err) {
        setError('Failed to fetch conference content.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const categories = [
    { id: 'all', name: 'Toutes les conférences' },
    { id: 'gps', name: 'Analyse GPS' },
    { id: 'training', name: 'Entraînement' },
    { id: 'prevention', name: 'Prévention' },
    { id: 'rehabilitation', name: 'Réathlétisation' },
    { id: 'analytics', name: 'Data Analytics' }
  ];

  const filteredUpcoming = selectedCategory === 'all' 
    ? upcomingConferences 
    : upcomingConferences.filter(conf => conf.category === selectedCategory);

  const filteredPast = selectedCategory === 'all' 
    ? pastConferences 
    : pastConferences.filter(conf => conf.category === selectedCategory);

  if (loading) {
    return <div>Loading conference content...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-400 to-black text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFB639]/20 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                  Les <span className="text-[#FFB639]">Conférences</span> FORMACADEMY
                </h1>
                <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed">
                  Échangez avec les meilleurs experts du football et restez à la pointe des dernières innovations.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/login"
                  className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#FFB639] transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Rejoindre les conférences
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <button className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-black transition-all duration-300">
                  <Bell className="mr-2 h-5 w-5" />
                  Alertes email
                </button>
              </div>

              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Play className="h-5 w-5 text-[#FFB639] mr-2" />
                  <span>En direct & replay</span>
                </div>
                <div className="flex items-center">
                  <MessageCircle className="h-5 w-5 text-[#FFB639] mr-2" />
                  <span>Q&R interactif</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img
                  src="https://images.pexels.com/photos/1181406/pexels-photo-1181406.jpeg"
                  alt="Conférence FORMACADEMY"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-[#FFB639] text-white p-6 rounded-xl shadow-lg">
                  <div className="text-2xl font-bold">50+</div>
                  <div className="text-sm">Experts</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-[#FFB639] text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Conferences */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Prochaines conférences
            </h2>
            <p className="text-xl text-gray-600">
              Ne manquez pas nos prochains événements avec les experts du football
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {filteredUpcoming.map((conference) => (
              <div key={conference.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="relative">
                  <img
                    src={conference.image}
                    alt={conference.title}
                    className="w-full h-48 object-cover"
                  />
                  {conference.isLive && (
                    <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                      <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
                      LIVE
                    </div>
                  )}
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-900">
                    {conference.price}
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(conference.date).toLocaleDateString('fr-FR')}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="h-4 w-4 mr-1" />
                      {conference.time}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                    {conference.title}
                  </h3>
                  
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-[#FFB639]/15 rounded-full flex items-center justify-center mr-3">
                      <span className="text-[#FFB639] font-bold text-sm">
                        {conference.speaker.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{conference.speaker}</div>
                      <div className="text-sm text-gray-600">{conference.speakerTitle}</div>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {conference.description}
                  </p>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <Users className="h-4 w-4 mr-1" />
                      {conference.attendees} inscrits
                    </div>
                    <div className="text-sm text-gray-500">
                      {conference.duration}
                    </div>
                  </div>
                  
                  <Link
                    to="/login"
                    className="w-full bg-[#FFB639] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#FFB639] transition-colors duration-300 text-center block"
                  >
                    S'inscrire
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Past Conferences */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Replays disponibles
            </h2>
            <p className="text-xl text-gray-600">
              Rattrapez les conférences que vous avez manquées
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filteredPast.map((conference) => (
              <div key={conference.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="relative">
                    <img
                      src={conference.image}
                      alt={conference.title}
                      className="w-full h-48 md:h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      <button className="w-16 h-16 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                        <Play className="h-8 w-8 text-[#FFB639] ml-1" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-[#FFB639]  mr-1" />
                        <span className="text-sm font-medium text-gray-700">{conference.rating}</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {conference.views} vues
                      </div>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                      {conference.title}
                    </h3>
                    
                    <div className="flex items-center mb-3">
                      <div className="w-8 h-8 bg-[#FFB639]/15 rounded-full flex items-center justify-center mr-2">
                      <span className="text-[#FFB639] font-bold text-xs">
                          {(conference.speaker || '').split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{conference.speaker}</div>
                        <div className="text-xs text-gray-600">{conference.speakerTitle}</div>
                      </div>
                    </div>

                    <p className="text-gray-600 mb-4 text-sm line-clamp-2">
                      {conference.description}
                    </p>

                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm text-gray-500">
                        {new Date(conference.date).toLocaleDateString('fr-FR')}
                      </div>
                      <div className="text-sm text-gray-500">
                        {conference.duration}
                      </div>
                    </div>
                    
                    <Link
                      to="/login"
                      className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors duration-300 text-center block text-sm"
                    >
                      Voir le replay
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experts Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nos experts
            </h2>
            <p className="text-xl text-gray-600">
              Rencontrez les professionnels qui animent nos conférences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {experts.map((expert) => (
              <div key={expert.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <img
                  src={expert.image}
                  alt={expert.name}
                  className="w-full h-64 object-cover"
                />
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {expert.name}
                  </h3>
                  <p className="text-[#FFB639] font-medium mb-2">
                    {expert.title}
                  </p>
                  <p className="text-gray-600 mb-4">
                    {expert.bio}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <span className="text-gray-500">Spécialité:</span>
                      <span className="font-medium text-gray-900 ml-1">{expert.speciality}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Exp:</span>
                      <span className="font-medium text-gray-900 ml-1">{expert.experience}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  MAG des Experts
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Recevez une alerte email pour chaque nouvelle conférence et ne manquez plus aucun événement avec nos experts.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <Bell className="h-5 w-5 text-[#FFB639] mr-2" />
                    Alertes conférences en temps réel
                  </li>
                  <li className="flex items-center">
                    <MessageCircle className="h-5 w-5 text-[#FFB639] mr-2" />
                    Questions/réponses en direct
                  </li>
                  <li className="flex items-center">
                    <Star className="h-5 w-5 text-[#FFB639] mr-2" />
                    Témoignages exclusifs
                  </li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <h4 className="text-xl font-bold text-gray-900 mb-4">
                  Inscription newsletter
                </h4>
                <form className="space-y-4">
                  <input
                    type="email"
                    placeholder="Votre adresse email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  />
                  <button
                    type="submit"
                    className="w-full bg-[#FFB639] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#FFB639] transition-colors duration-300"
                  >
                    S'abonner aux alertes
                  </button>
                </form>
                <p className="text-xs text-gray-500 mt-3">
                  Désabonnement possible à tout moment. Vos données sont protégées.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-black to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Rejoignez notre communauté d'experts
          </h2>
          <p className="text-xl text-[#FFB639] mb-8 max-w-3xl mx-auto">
            Participez aux conférences, échangez avec les meilleurs et restez à la pointe de l'innovation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#FFB639] transition-all duration-300 transform hover:scale-105"
            >
              Accéder aux conférences
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <button className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-black transition-all duration-300">
              Devenir expert
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShowcaseConferences;
